/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { usePrompts } from '@/hooks/usePrompts';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { CustomButton, SearchInput } from '@/components/common';

export const ConversationIdSearch = ({
  setIsOverlayActive,
  isPromptSearch,
  initialValue = '',
}: {
  setIsOverlayActive: (value: boolean) => void;
  isPromptSearch?: boolean;
  initialValue?: string;
}) => {
  const prompts = usePrompts();
  const fetchByConversationId = prompts?.fetchPromptsByConversationId;
  const clearPrompts = prompts?.clearPrompts;
  const fetchPrompts = prompts?.fetchPrompts;
  const { state } = usePrompts();
  const { messages } = useLanguage();
  const promptFilterIntl = messages?.PromptFilters;

  const FormSchema = z.object({
    conversationId: z.string(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      conversationId: initialValue,
    },
  });

  useEffect(() => {
    if (initialValue) {
      form.reset({ conversationId: initialValue });
    }
  }, [initialValue]);

  const { formState, reset, watch } = form;
  const { isDirty } = formState;

  useEffect(() => {
    setIsOverlayActive(isDirty);
  }, [isDirty]);

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    reset(data);
    if (isPromptSearch && fetchByConversationId) {
      await fetchByConversationId(data.conversationId);
    }
  };

  const conversationIdValue = watch('conversationId');

  const handleClear = () => {
    reset({ conversationId: '' });
    // delete scope_id from url
    const url = new URL(window.location.href);
    url.searchParams.delete('scope_id');
    window.history.replaceState(null, '', url.toString());
    if (isPromptSearch && clearPrompts) clearPrompts();
    fetchPrompts(state.queryParams);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex">
          <FormField
            control={form.control}
            name="conversationId"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormControl>
                  <SearchInput
                    onClear={handleClear}
                    placeholder={promptFilterIntl?.conversation_id_placeholder}
                    className="h-7 rounded-none rounded-l-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <CustomButton
            type="submit"
            className="h-7 rounded-r-sm text-form-input-text font-medium text-body-secondary"
            disabled={conversationIdValue.length < 3}
          >
            {promptFilterIntl?.go}
          </CustomButton>
        </div>
      </form>
    </Form>
  );
};
